import React from 'react';
import { t } from '@lingui/macro';
import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Heading,
  HStack,
  Stack,
  Text,
  useColorMode,
} from '@cardboard-ui/react';
import {
  CalendarDayIcon,
  FolderOpenIcon,
  NavigateForwardIcon,
} from 'components/icons';
import { useOpenFolder } from 'components/FolderViewer';
import SpaceIcon from 'components/SpaceIcon';
import { generatePath } from 'react-router-dom';
import { EVENT_PATH, SPACE_PATH } from 'utils/routes';
import { Node } from './SpaceItemAccessDetails';

type LocationBreadcrumbType = NonNullable<
  NonNullable<
    NonNullable<NonNullable<Node['ownershipPath']>['edges']>[0]
  >['node']
>;

interface LocationInfoProps {
  ownershipPathNodes: LocationBreadcrumbType[];
}
export const LocationInfo = ({ ownershipPathNodes }: LocationInfoProps) => {
  return (
    <Stack>
      <Heading size="sm">{t`Location`}</Heading>
      <Breadcrumb
        separator={<NavigateForwardIcon size="sm" color="gray.500" />}
        overflowX="auto"
      >
        {ownershipPathNodes.map((node) => {
          if (!('id' in node)) return null;
          return (
            <BreadcrumbItem key={node.id}>
              <LocationBreadcrumb node={node} />
            </BreadcrumbItem>
          );
        })}
      </Breadcrumb>
    </Stack>
  );
};
const LocationBreadcrumb = ({ node }: { node?: LocationBreadcrumbType }) => {
  const { openFolderTo } = useOpenFolder();

  const { colorMode } = useColorMode();

  if (!node || !('id' in node)) return null;

  switch (node.__typename) {
    case 'Space':
      return (
        <BreadcrumbLink to={generatePath(SPACE_PATH, { spaceId: node.id })}>
          <BreadcrumbContent
            icon={
              <SpaceIcon name={node.icon.name} color={node.color[colorMode]} />
            }
            name={node.name}
          />
        </BreadcrumbLink>
      );
    case 'Event':
      return (
        <BreadcrumbLink to={generatePath(EVENT_PATH, { eventId: node.id })}>
          <BreadcrumbContent icon={<CalendarDayIcon />} name={node.name} />
        </BreadcrumbLink>
      );
    case 'Folder':
      return (
        <BreadcrumbLink to={openFolderTo(node.id)}>
          <BreadcrumbContent icon={<FolderOpenIcon />} name={node.name} />
        </BreadcrumbLink>
      );
  }
};
interface BreadcrumbContentProps {
  name: string;
  icon: React.ReactNode;
}

const BreadcrumbContent = ({ icon, name }: BreadcrumbContentProps) => {
  return (
    <HStack spacing={1}>
      <Box color="blue.500">{icon}</Box>
      <Text>{name}</Text>
    </HStack>
  );
};
